import { EVENT_NAME as SYNC_STATE_CHANGE_EVENT } from '@adobe-fonts/site/events/SyncStateChangeEvent.js';

import { getService, scopeApply } from '../../../util/angular_helpers.js';
import { syncVariations, unsyncVariations } from '../../../util/Sync.js';
import { sendAnalyticsEventWithPage } from '../../../util/new_relic.js';
import { getData } from '../../../util/script_data.js';

export const EVENT_NAME = 'af.variation-card-sync';

export class VariationCardSync {
  constructor(element) {
    this.element = element;
  }

  handleSyncStateChangeEvent = (event) => {
    const button = event.target;
    const card = event.target.closest('af-variation-card, .font-variation-card');
    if (!card) {
      return;
    }

    this.dataAnalytics = {
      fontVariationId: card.dataset.id,
      fontVariationName: card.dataset.variationName,
      familySlug: card.dataset.familySlug
    };

    if (button.synced) {
      this.unsyncVariation(button, card);
    } else {
      sendAnalyticsEventWithPage('sync-font-button.click', this.dataAnalytics);
      this.syncVariation(button, card);
    }
  };

  async syncVariation(button, card) {
    const id = parseInt(card.dataset.id, 10);
    if (!id) {
      return;
    }

    try {
      await this.authenticateSyncVariation(id);
      button.synced = true;
      button.toggleAttribute('synced', true);
      this.element.dispatchEvent(new CustomEvent(EVENT_NAME, { synced: true }));
      this.showSyncSuccessMessage(card);
    } catch (e) {
      this.showSyncErrorMessage(card);
    }
  }

  async authenticateSyncVariation(id) {
    const authService = getService('AuthenticationService');
    await authService.handleAuthenticationWithPostLoginAction('activate_font', {
      font_variation_id: id
    });

    await syncVariations([id]);
  }

  showSyncSuccessMessage(card) {
    const i18n = getData('/neue/i18n');
    const firstMileService = getService('FirstMileService');
    firstMileService.onUseModelFontActivation(i18n, {
      queryParams: {
        familyName: card.dataset.familyName
      }
    });
  }

  showSyncErrorMessage(card) {
    const notificationService = getService('NotificationService');
    const i18n = getData('/neue/i18n');
    scopeApply(() => {
      notificationService.error(
        i18n['neue.browse.family_card']['error_occurred_adding_font'], {
          actionLabel: i18n['neue.browse.family_card']['try_again'],
          callbackAction: () => this.syncVariation(card)
        });
    });
  }

  async unsyncVariation(button, card) {
    const id = parseInt(card.dataset.id, 10);
    if (!id) {
      return;
    }

    await this.confirmVariationUsync();
    try {
      this.unsyncVariationId(id);
      button.synced = false;
      button.toggleAttribute('synced', false);
      this.element.dispatchEvent(new CustomEvent(EVENT_NAME, { synced: false }));
      this.showUnsyncSuccessMessage();
      sendAnalyticsEventWithPage('unsync-font-button.click', this.dataAnalytics);
    } catch (e) {
      this.showUnsyncErrorMessage();
    }
  }

  async unsyncVariationId(id) {
    await unsyncVariations([id]);
  }

  async confirmVariationUsync() {
    return new Promise(resolve => {
      const firstMileService = getService('FirstMileService');
      firstMileService.shouldShowRemoveFamilyConfirmationDialog().then(shouldConfirm => {
        if (shouldConfirm) {
          const i18n = getData('/neue/i18n');
          const header = i18n['neue.browse.family_card']['remove_font_confirmation_header'];
          const description = i18n['neue.browse.family_card']['remove_font_confirmation_description'];
          firstMileService.showRemoveFamilyConfirmationDialog(header, description, resolve);
        } else {
          resolve();
        }
      });
    });
  }

  showUnsyncSuccessMessage() {
    const i18n = getData('/neue/i18n');
    const notificationService = getService('NotificationService');
    scopeApply(() => {
      notificationService.notice(i18n['neue.browse.family_card']['single_font_removed_success_message']);
    });
  }

  showUnsyncErrorMessage() {
    const i18n = getData('/neue/i18n');
    const notificationService = getService('NotificationService');

    scopeApply(() => notificationService.error(
      i18n['neue.browse.family_card']['error_occurred_removing_font']));
  }
}

export function registerEventHandlers(element) {
  const variationSync = new VariationCardSync(element);
  element.addEventListener(
    SYNC_STATE_CHANGE_EVENT, variationSync.handleSyncStateChangeEvent);
}
